import React, { useState, useEffect } from 'react';
import { Form, Progress } from 'antd';
import Cookies from 'js-cookie';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import FormLabelWithField from 'components/Form/FormLabelWithField';
import TextField from 'components/Form/TextField';
import Button from 'components/Button';
import Select from 'components/Form/Select';
import Text from 'components/Text';
import { industryList, otherIndustryList } from 'components/Form/utils/generalFormData';
import { FormRedirect } from 'components/Form/DemoForm/FormRedirect';
import ModalMarketingForm from 'components/ModalMarketingForm';
import { STIcon } from 'components/Brand/Icons/STIcon';
import { saveForm, init } from '../../../services/marketoService';
import { calculateROI } from './calculations';
import { ROIFormContainer, ROIResultContainer } from './styles';
import { ToolSectionContainer, ToolSectionFormStep, ToolSectionProgressArea } from '../styles';
import { Revenue, CallPercentage, SoldPercentage } from './utils/content';

const ToolROISection = ({ marketoFormId = 1322, ...otherProps }) => {
  const [isUpdate, setUpdate] = useState(false);
  const [isSubmitted, setSubmitted] = useState(false);
  const [isMarketingModalOpen, toggleIsMarketingModalOpen] = useState(false);

  const [formValues, setFormValues] = useState({
    company: '',
    techs: '',
    officeStaff: '',
    revenue: 0,
    calls: 0,
    estimates: 0,
    leads: 0,
    jobs: 0,
    email: '',
    industry: '',
    otherIndustry: '',
    existingCustomer: '',
  });

  const [formValid, setFormValid] = useState({
    company: 0,
    techs: 0,
    officeStaff: 0,
    revenue: 0,
    calls: 0,
    estimates: 0,
    leads: 0,
    jobs: 0,
    email: 0,
    industry: 0,
    otherIndustry: 0,
    existingCustomer: 0,
  });

  const nameType = {
    industry: { step: 0, validateType: 'NOT_EMPTY' },
    otherIndustry: { step: 0, validateType: 'NOT_EMPTY' },
    techs: { step: 0, validateType: 'GREATER_THAN_ZERO' },
    officeStaff: { step: 0, validateType: 'GREATER_THAN_ZERO' },
    revenue: { step: 1, validateType: 'GREATER_THAN_ZERO' },
    calls: { step: 1, validateType: 'GREATER_THAN_ZERO' },
    estimates: { step: 2, validateType: 'GREATER_THAN_ZERO' },
    leads: { step: 2, validateType: 'GREATER_THAN_ZERO' },
    jobs: { step: 2, validateType: 'GREATER_THAN_ZERO' },
    company: { step: 3, validateType: 'NOT_EMPTY' },
    email: { step: 3, validateType: 'EMAIL' },
    existingCustomer: { step: 3, validateType: 'NOT_EMPTY' },
  };

  const [results, setResults] = useState([
    {
      title: 'ROI with ServiceTitan',
      value: '0%',
      active: false,
    },
    {
      title: 'Monthly Revenue Increase',
      value: '$0k',
      active: true,
    },
    {
      title: 'Annual Revenue Increase',
      value: '0%',
      active: false,
    },
  ]);

  let slider;
  const [formStep, setFormStep] = useState(0);

  const sliderSettings = {
    dots: false,
    arrows: false,
    infinite: false,
    draggable: false,
    touchMove: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true,
    accessibility: true,
  };

  useEffect(() => {
    init(marketoFormId);
  }, []);

  const checkFieldValidate = (id, value) => {
    const fieldValidation = formValid;

    fieldValidation[id] = 1;

    if (nameType[id].validateType === 'GREATER_THAN_ZERO') {
      fieldValidation[id] = parseInt(value, 10) > 0 ? 1 : -1;
    } else if (nameType[id].validateType === 'EMAIL') {
      fieldValidation[id] = value.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i) ? 1 : -1;
    } else if (nameType[id].validateType === 'NOT_EMPTY') {
      fieldValidation[id] = value !== '' ? 1 : -1;
    }

    if (id === 'otherIndustry') {
      fieldValidation[id] = formValues.industry !== 'Other' || value !== '' ? 1 : -1;
    }

    setFormValid(fieldValidation);
    setUpdate(!isUpdate);

    return fieldValidation[id];
  };

  const checkStepValidate = () => {
    const keys = Object.keys(nameType).filter(x => nameType[x].step === formStep);
    const stepValid = keys.filter(element => checkFieldValidate(element, formValues[element]) === 1)
      .length;
    return stepValid === keys.length;
  };

  const calculate = () => {
    const results = calculateROI(formValues);
    submit(results);
    setResults(results);
  };

  const onChange = (id, value) => {
    const tempValues = { ...formValues };
    tempValues[id] = value;
    setFormValues(tempValues);
  };

  const prevStep = () => {
    const newStep = formStep - 1;

    setFormStep(newStep);

    if (newStep === 0) {
      slider.slickGoTo(newStep);
    } else {
      slider.slickPrev();
    }
  };

  const nextStep = async () => {
    if (!checkStepValidate()) {
      return;
    }

    const newStep = formStep + 1;
    if (newStep === 4) {
      calculate();
    }

    setFormStep(newStep);

    if (formStep === 0) {
      slider.slickGoTo(newStep);
    } else {
      slider.slickNext();
    }
  };

  const afterChangeHandler = currentSlide => {
    setFormStep(currentSlide);
  };

  const submit = async calcResult => {
    dataLayer.push({
      event: 'calculator',
      calculator_type: 'ROI Calculator - entries',
      company: formValues.company,
      no_of_technicians: formValues.techs,
      average_ticket_size: formValues.revenue,
      average_calls_per_day: formValues.calls,
      booked_calls_percentage: `${formValues.leads}%`,
      leads_to_estimates_percentage: `${formValues.estimates}%`,
      sold_jobs_percentage: `${formValues.jobs}%`,
      no_of_office_staff: formValues.officeStaff,
      email: formValues.email,
      industry: formValues.industry,
      other_industry: formValues.otherIndustry,
      existing_ST_user: formValues.existingCustomer,
    });

    Cookies.set('email', formValues.email);
    Cookies.set('company', formValues.company);
    Cookies.set('techs', formValues.techs);
    Cookies.set('officeStaff', formValues.officeStaff);

    await saveForm({
      ...formValues,
      roiCalcWithST: calcResult[0].value,
      roiCalcMonthlyRevenue: calcResult[1].value,
      roiCalcAnnualRevenue: calcResult[2].value,
      marketoFormId,
    });
  };

  const handleContactMe = () => {
    toggleIsMarketingModalOpen(true);
  };

  const handleContactMeModalSubmit = () => {
    setSubmitted(true);
    toggleIsMarketingModalOpen(false);

    FormRedirect('clearbit', {
      techs: formValues.techs,
      officeStaff: formValues.officeStaff,
      industry: formValues.industry,
      otherIndustry: formValues.otherIndustry,
      typeOfBusiness: 'Construction',
      email: formValues.email,
    });
  };

  return (
    <ToolSectionContainer data-testid="toolRoiSection-container">
      <ROIFormContainer sectionContainer={true} {...otherProps}>
        {formStep === 4 && (
          <div className="roi-form-section--back-icon" onClick={prevStep}>
            <STIcon type="icon-arrow-left" />
          </div>
        )}
        <Text type="h1" className="roi-form-section--title">
          {otherProps?.hero?.heading}
        </Text>
        <Form className="roi-form--form" data-position="roi form">
          <Slider ref={c => (slider = c)} {...sliderSettings} afterChange={afterChangeHandler}>
            <ToolSectionFormStep className="data-layer-form">
              <Form.Item className="roi-form--form-item" style={{ flex: 7 }}>
                <FormLabelWithField
                  label="What industry do you do the most business in?"
                  helperText=""
                >
                  <Select
                    id="industry"
                    placeholder="Select Industry"
                    options={industryList.slice(1)}
                    handleChange={onChange}
                    formValid={formValid}
                  />
                </FormLabelWithField>
                {formValues.industry === 'Other' && (
                  <FormLabelWithField>
                    <Select
                      id="otherIndustry"
                      placeholder="Select from other Industries"
                      options={otherIndustryList.slice(1)}
                      handleChange={onChange}
                      formValid={formValid}
                    />
                  </FormLabelWithField>
                )}
              </Form.Item>
              <Form.Item className="roi-form--form-item" style={{ flex: 4 }}>
                <FormLabelWithField
                  label="Number of techs"
                  helperText="How many full-time technicians (e.g. plumbers, or electricians) work at your company? For best results, omit apprentices and helpers."
                >
                  <TextField
                    id="techs"
                    type="number"
                    formValid={formValid}
                    handleChange={onChange}
                    showOnlyFormError
                  />
                </FormLabelWithField>
              </Form.Item>
              <Form.Item className="roi-form--form-item" style={{ flex: 4 }}>
                <FormLabelWithField
                  label="Number of office staff"
                  helperText="How many full-time office employees (eg. manager, dispatchers, CSRs, etc) work at your company?"
                >
                  <TextField
                    id="officeStaff"
                    type="number"
                    formValid={formValid}
                    handleChange={onChange}
                    showOnlyFormError
                  />
                </FormLabelWithField>
              </Form.Item>
            </ToolSectionFormStep>
            <ToolSectionFormStep className="data-layer-form">
              <Form.Item className="roi-form--form-item">
                <FormLabelWithField label="Average ticket value?" helperText="">
                  <Select
                    id="revenue"
                    placeholder="Select amount ($)"
                    options={Revenue}
                    handleChange={onChange}
                    formValid={formValid}
                  />
                </FormLabelWithField>
              </Form.Item>
              <Form.Item className="roi-form--form-item">
                <FormLabelWithField label="Average number of calls per day?" helperText="">
                  <TextField
                    id="calls"
                    type="number"
                    formValid={formValid}
                    handleChange={onChange}
                    showOnlyFormError
                  />
                </FormLabelWithField>
              </Form.Item>
            </ToolSectionFormStep>
            <ToolSectionFormStep className="data-layer-form">
              <Form.Item className="roi-form--form-item">
                <FormLabelWithField label="Percent of calls booked?" helperText="">
                  <Select
                    id="leads"
                    placeholder="Select percentage (%)"
                    options={CallPercentage}
                    handleChange={onChange}
                    formValid={formValid}
                  />
                </FormLabelWithField>
              </Form.Item>
              <Form.Item className="roi-form--form-item">
                <FormLabelWithField label="Percent turned into estimates?" helperText="">
                  <Select
                    id="estimates"
                    placeholder="Select percentage (%)"
                    options={CallPercentage}
                    handleChange={onChange}
                    formValid={formValid}
                  />
                </FormLabelWithField>
              </Form.Item>
              <Form.Item className="roi-form--form-item">
                <FormLabelWithField label="Percent of turned into sold jobs?" helperText="">
                  <Select
                    id="jobs"
                    placeholder="Select percentage (%)"
                    options={SoldPercentage}
                    handleChange={onChange}
                    formValid={formValid}
                  />
                </FormLabelWithField>
              </Form.Item>
            </ToolSectionFormStep>
            <ToolSectionFormStep className="data-layer-form">
              <Form.Item className="roi-form--form-item">
                <FormLabelWithField label="Name of your company?" helperText="">
                  <TextField
                    id="company"
                    type="text"
                    values={formValues}
                    formValid={formValid}
                    handleChange={onChange}
                    showOnlyFormError
                  />
                </FormLabelWithField>
              </Form.Item>
              <Form.Item className="roi-form--form-item">
                <FormLabelWithField label="Business email address?" helperText="">
                  <TextField
                    id="email"
                    type="text"
                    values={formValues}
                    formValid={formValid}
                    handleChange={onChange}
                    showOnlyFormError
                  />
                </FormLabelWithField>
              </Form.Item>
              <Form.Item className="roi-form--form-item">
                <FormLabelWithField label="Existing ServiceTitan Customer?" helperText="">
                  <Select
                    id="existingCustomer"
                    placeholder="Yes or No"
                    options={[
                      { label: 'Yes', value: 0 },
                      { label: 'No', value: 1 },
                    ]}
                    handleChange={onChange}
                    formValid={formValid}
                  />
                </FormLabelWithField>
              </Form.Item>
            </ToolSectionFormStep>
            <ROIResultContainer sectionContainer={true} className="section-roi-result-hero--main">
              <div className="section-roi-result-hero--result-container">
                {results &&
                  results.map((item, index) => {
                    return (
                      <div
                        className={`section-roi-result-hero--result-item${
                          item.active ? ' active' : ''
                        }`}
                        key={index}
                      >
                        <Text type="p" className="section-roi-result-hero--result-item-title">
                          {item.title}
                        </Text>
                        <Text type="h2" className="section-roi-result-hero--result-item-value">
                          {item.value}
                        </Text>
                      </div>
                    );
                  })}
              </div>
            </ROIResultContainer>
          </Slider>

          <div
            className="roi-form-section--button-container"
            style={{ justifyContent: formStep === 0 ? 'flex-end' : 'center' }}
          >
            {formStep === 0 ? (
              <Button
                className="roi-form-section--next-button"
                type="secondary"
                shadow={false}
                button
                width="100%"
                onClick={nextStep}
              >
                Get Started
              </Button>
            ) : formStep < 4 ? (
              <>
                <Button
                  className="roi-form-section--prev-button"
                  type="Transparent - Black Text - No Border"
                  shadow={false}
                  button
                  width="100%"
                  onClick={prevStep}
                >
                  Back
                </Button>
                <Button
                  className="roi-form-section--next-button"
                  type="secondary"
                  shadow={false}
                  button
                  width="100%"
                  onClick={nextStep}
                >
                  Continue
                </Button>
              </>
            ) : formValues.existingCustomer === 1 ? (
              <div className="roi-form-section--last-button-container">
                {!isSubmitted ? (
                  <Text type="p" className="roi-form-section--contact-label">
                    Would you like to connect with a rep for a growth consultation?
                  </Text>
                ) : (
                  <Text type="p" className="roi-form-section--contact-label label-success">
                    Thank you for your interest in ServiceTitan! Someone will be reaching out
                    shortly.
                  </Text>
                )}
                <Button
                  className="roi-form-section--contact-button"
                  type="primary"
                  shadow={false}
                  button
                  width="100%"
                  onClick={handleContactMe}
                  disabled={isSubmitted}
                >
                  Yes, Contact me
                </Button>
              </div>
            ) : null}
          </div>

          {formStep > 0 && formStep < 4 && (
            <ToolSectionProgressArea>
              <Progress
                percent={formStep * 25}
                showInfo={false}
                strokeWidth={6}
                strokeColor="#4AB971"
              />
            </ToolSectionProgressArea>
          )}
        </Form>

        <ModalMarketingForm
          isModalOpen={isMarketingModalOpen}
          onModalCancel={() => toggleIsMarketingModalOpen(false)}
          onFormSuccess={handleContactMeModalSubmit}
          useEmbeddedForm={true}
          shouldRedirect={false}
          formTitle={'Request your personalized demo'}
        />
      </ROIFormContainer>
    </ToolSectionContainer>
  );
};

export default ToolROISection;
